import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { createNewPlan, copyPlanRoutines } from "@/components/routines/search/hooks/utils";

export const useWorkoutPlanSubscriptions = () => {
  const queryClient = useQueryClient();

  const subscribeMutation = useMutation({
    mutationFn: async (planId: string) => {
      console.log("Creating personal copy of workout plan:", planId);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      // Get the original plan details
      const { data: originalPlan, error: planError } = await supabase
        .from("workout_plans")
        .select("name, description")
        .eq("id", planId)
        .single();

      if (planError) throw planError;

      // Create personal copy of the plan
      const { data: newPlan, error: createError } = await supabase
        .from("workout_plans")
        .insert({
          name: `${originalPlan.name} (Copia Personal)`,
          description: originalPlan.description,
          created_by: user.id,
          is_personal: false,
          copied_from_plan_id: planId
        })
        .select()
        .single();

      if (createError) throw createError;

      // Copy all routines from original plan to new plan
      await copyPlanRoutines(planId, newPlan.id, user.id);

      return newPlan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workout-plans"] });
      toast({
        title: "¡Plan copiado con éxito!",
        description: "Ahora tienes una copia personal editable del plan.",
      });
    },
    onError: (error) => {
      console.error("Error al copiar el plan:", error);
      toast({
        title: "Error al copiar plan",
        description: "No se pudo crear la copia personal del plan. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async (planId: string) => {
      console.log("Deleting personal copy of workout plan:", planId);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      // Delete the personal copy (user can only delete their own plans)
      const { error } = await supabase
        .from("workout_plans")
        .delete()
        .eq("id", planId)
        .eq("created_by", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workout-plans"] });
      toast({
        title: "Plan eliminado con éxito",
        description: "Has eliminado tu copia personal del plan.",
      });
    },
    onError: (error) => {
      console.error("Error al eliminar la copia del plan:", error);
      toast({
        title: "Error al eliminar plan",
        description: "No se pudo eliminar la copia personal del plan. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    },
  });

  return {
    subscribeMutation,
    unsubscribeMutation,
  };
};